import { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useSaveInquiry } from '@/api/contact';
import mainBanner from '@/assets/img/banner/contact.jpg';
import { PrimaryButton } from '@/components/common/Button/style';
import SubBanner from '@/components/common/Banner/SubBanner/SubBanner';
import Field from '@/components/common/Field/Field';
import { EmailRow } from '@/components/common/form/formPageStyles';
import InputFiles from '@/components/common/Input/InputFiles/InputFiles';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import InputTextarea from '@/components/common/Input/InputTextarea/InputTextarea';
import { emailOptions } from '@/constants/option';
import { useToastStore } from '@/store/toastStore';

import * as S from './style';

interface FormData {
	name: string;
	emailId: string;
	emailDomain: string;
	emailDomainSelect: string;
	phone: string;
	title: string;
	content: string;
}
interface FormErrors {
	name?: boolean;
	email?: boolean;
	phone?: boolean;
	title?: boolean;
	content?: boolean;
}

const Contact = () => {
	const { t } = useTranslation();
	const [formData, setFormData] = useState<FormData>({
		name: '',
		emailId: '',
		emailDomain: '',
		emailDomainSelect: '직접입력',
		phone: '',
		title: '',
		content: '',
	});

	const [errors, setErrors] = useState<FormErrors>({});
	const [submitted, setSubmitted] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

	const { openToast } = useToastStore();
	const navigate = useNavigate();
	const { mutateAsync: saveInquiry } = useSaveInquiry();

	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;

		if (name === 'phone') {
			setFormData({ ...formData, [name]: value.replace(/\D/g, '') });
		} else {
			setFormData({ ...formData, [name]: value });
		}

		if (submitted) {
			const newErrors = { ...errors };
			if (name === 'name' && value) delete newErrors.name;
			if ((name === 'emailId' || name === 'emailDomain') && formData.emailId && formData.emailDomain) delete newErrors.email;
			if (name === 'phone' && value) delete newErrors.phone;
			if (name === 'title' && value) delete newErrors.title;
			if (name === 'content' && value) delete newErrors.content;
			setErrors(newErrors);
		}
	};

	const validateForm = (): FormErrors => {
		const newErrors: FormErrors = {};
		if (!formData.name) newErrors.name = true;
		if (!formData.emailId || !formData.emailDomain) newErrors.email = true;
		if (!formData.phone) newErrors.phone = true;
		if (!formData.title) newErrors.title = true;
		if (!formData.content) newErrors.content = true;
		return newErrors;
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formErrors = validateForm();
		setErrors(formErrors);
		setSubmitted(true);

		if (Object.keys(formErrors).length > 0) return;

		const form = new FormData();
		form.append('inquiry_name', formData.name);
		form.append('inquiry_email', `${formData.emailId}@${formData.emailDomain}`);
		form.append('inquiry_cell', formData.phone);
		form.append('inquiry_title', formData.title);
		form.append('inquiry_content', formData.content);
		uploadedFiles.forEach((file, index) => {
			form.append(`file[${index}]`, file);
		});

		try {
			const res = await saveInquiry(form);
			if (res.data.success) {
				openToast({ icon: 'alert', content: t('제출이 완료되었습니다.'), showTime: 3000 });
				setTimeout(() => navigate('/'), 3000);
			} else {
				openToast({ icon: 'alert', content: t('제출에 실패하였습니다.'), showTime: 3000 });
			}
		} catch (error) {
			console.error('Error:', error);
		}

		setFormData({
			name: '',
			emailId: '',
			emailDomain: '',
			emailDomainSelect: '직접입력',
			phone: '',
			title: '',
			content: '',
		});
		setSubmitted(false);
	};

	const isEmailDomainReadonly = formData.emailDomainSelect !== '' && formData.emailDomainSelect !== '직접입력';

	return (
		<main>
			<SubBanner subTitle="contact.banner.subtitle" title="contact.banner.title" img={mainBanner} />
			<S.FormPage>
				<S.Form onSubmit={handleSubmit} noValidate>
					<Field title="이름" error={submitted && errors.name ? '이름을 입력해 주세요.' : ''}>
						<InputText
							name="name"
							placeholder="contact.placeholders.name"
							value={formData.name}
							input={handleChange}
							isError={submitted && !!errors.name}
							maxLength={20}
						/>
					</Field>

					<Field title="이메일" error={submitted && errors.email ? '이메일을 입력해 주세요.' : ''}>
						<EmailRow>
							<InputText
								name="emailId"
								placeholder="contact.placeholders.emailId"
								value={formData.emailId}
								input={handleChange}
								isError={submitted && !!errors.email}
							/>
							<span>@</span>
							<InputText
								name="emailDomain"
								placeholder="contact.placeholders.emailDomain"
								value={formData.emailDomain}
								input={handleChange}
								readonly={isEmailDomainReadonly}
								isError={submitted && !!errors.email}
							/>
							<InputSelect
								options={emailOptions}
								selectedOption={formData.emailDomainSelect}
								setSelectedOption={(val) => {
									setFormData({
										...formData,
										emailDomainSelect: val,
										emailDomain: val !== '직접입력' ? val : formData.emailDomain,
									});
								}}
								defaultValue="common.directInput"
							/>
						</EmailRow>
					</Field>

					<Field title="전화번호" error={submitted && errors.phone ? '전화번호를 입력해 주세요.' : ''}>
						<InputText
							type="tel"
							name="phone"
							placeholder="common.placeholders.phone"
							value={formData.phone}
							input={handleChange}
							isError={submitted && !!errors.phone}
							maxLength={11}
						/>
					</Field>

					<Field title="제목" error={submitted && errors.title ? '제목을 입력해 주세요.' : ''}>
						<InputText
							name="title"
							placeholder="common.placeholders.title"
							value={formData.title}
							input={handleChange}
							isError={submitted && !!errors.title}
							maxLength={50}
						/>
					</Field>

					<Field title="내용" error={submitted && errors.content ? '내용을 입력해 주세요.' : ''}>
						<InputTextarea
							name="content"
							placeholder="common.placeholders.content"
							value={formData.content}
							input={handleChange}
							isError={submitted && !!errors.content}
							maxLength={2000}
							minHeight="38.4rem"
						/>
					</Field>

					<Field title="첨부파일">
						<InputFiles files={uploadedFiles} setFiles={setUploadedFiles} />
					</Field>

					<S.FormActions>
						<PrimaryButton
							type="submit"
							disabled={
								!formData.name ||
								!formData.emailId ||
								!formData.emailDomain ||
								!formData.title ||
								!formData.phone ||
								!formData.content
							}
						>
							{t('common.actions.register')}
						</PrimaryButton>
					</S.FormActions>
				</S.Form>
			</S.FormPage>
		</main>
	);
};

export default Contact;
